import {AfterViewInit, Component, OnDestroy, QueryList, ViewChildren} from '@angular/core';
import {GeneralPrePhotosModel} from '../../../../../../models/data-models/general-pre-photos-model';
import {PhotosModel} from '../../../../../../models/data-models/photos-model';
import {NgModel} from '@angular/forms';
import {Subscription} from 'rxjs';
import {ShareService} from "../../../../../../services/share.service";
import {ValidationService} from "../../../../../../services/validation.service";
import {debounceTime} from "rxjs/operators";

@Component({
  selector: 'app-install-general-pre-photos',
  templateUrl: './install-general-pre-photos.component.html',
  styleUrls: ['./install-general-pre-photos.component.scss'],
})
export class InstallGeneralPrePhotosComponent implements AfterViewInit, OnDestroy {
  public model: GeneralPrePhotosModel;
  public photosModel: PhotosModel;

  @ViewChildren(NgModel) inputs: QueryList<NgModel>;

  private readonly modelSub: Subscription;
  private readonly photosSub: Subscription;
  private inputSubscriptions: Subscription[] = [];

  constructor(
    public shareService: ShareService,
    public validationService: ValidationService
  ) {
    this.model = this.shareService.generalPrePhotos || new GeneralPrePhotosModel();
    this.ensureModelDefaults();
    this.modelSub = this.shareService.generalPrePhotos$.subscribe((data) => {
      this.model = data || new GeneralPrePhotosModel();
      this.ensureModelDefaults();
    });

    this.photosModel = this.shareService.photos || new PhotosModel();
    this.photosSub = this.shareService.photos$.subscribe((data) => {
      this.photosModel = data || new PhotosModel();
    });
  }

  ngAfterViewInit(): void {
    this.inputs.forEach(input => {
      this.inputSubscriptions.push(input.control.valueChanges.pipe(
        debounceTime(500)
      ).subscribe(() => {
        this.shareService.generalPrePhotos = this.model;
      }));
    });

    // Subscribe to changes in the list of inputs
    // (it is called if some input is added or removed from the form/page,
    // for example, because of *ngIf condition changes)
    this.inputs.changes.subscribe(() => {
      // Unsubscribe from old subscriptions
      this.inputSubscriptions.forEach(subscription => subscription.unsubscribe());

      // Subscribe to new inputs
      this.inputs.forEach(input => {
        this.inputSubscriptions.push(input.control.valueChanges.pipe(
          debounceTime(500)
        ).subscribe(() => {
          this.shareService.generalPrePhotos = this.model;
        }));
      });
    });
  }

  private ensureModelDefaults(): void {
    // Ensure arrays have minimum entries for display
    while (this.model.preInstallBoilerRoomImages.length < 3) {
      this.model.preInstallBoilerRoomImages.push({image: null});
    }

    if (this.model.preInstallHeatingImages.length === 0) {
      this.model.preInstallHeatingImages = [{image: null}];
      return;
    }

    if (this.model.preInstallHeatingImages[this.model.preInstallHeatingImages.length - 1]?.image !== null) {
      this.model.preInstallHeatingImages.push({image: null});
    }
  }


  updateHeatingImages(value: any, index: number) {
    if (value === null) {
      this.model.preInstallHeatingImages.splice(index, 1);
      this.removePostInstallHeatingImage();
      this.addLastNullForPostInstallHeatingImage();

      if (this.model.preInstallHeatingImages.length === 0) {
        this.model.preInstallHeatingImages.push({image: null});
      }
    } else {
      if (this.model.preInstallHeatingImages[this.model.preInstallHeatingImages.length - 1]?.image !== null) {
        this.model.preInstallHeatingImages.push({image: null});
        if (this.photosModel.postInstallHeatingImages.length < this.model.preInstallHeatingImages.length - 1) {
          this.photosModel.postInstallHeatingImages.push({image: null});
        }
      }
    }

    this.shareService.photos = this.photosModel;
  }

  private removePostInstallHeatingImage() {
    let nullIndex = -1;
    if (this.photosModel?.postInstallHeatingImages && this.model.preInstallHeatingImages.length >= 1) {
      for (let i = 0; i < this.photosModel?.postInstallHeatingImages?.length; i++) {
        if (this.photosModel.postInstallHeatingImages[i].image === null) {
          nullIndex = i;
          break;
        }
      }
    }
    if (nullIndex !== -1) {
      this.photosModel.postInstallHeatingImages.splice(nullIndex, 1);
    } else {
      this.photosModel.postInstallHeatingImages.splice(0, 1);
    }
    this.shareService.photos = this.photosModel;
  }

  private addLastNullForPostInstallHeatingImage() {
    let areAllNecessaryImagesFilledOut = true;
    for (let i = 0; i < this.model.preInstallHeatingImages.length - 1; i++) {
      if (this.photosModel?.postInstallHeatingImages[i]?.image === null) {
        areAllNecessaryImagesFilledOut = false;
        break;
      }
    }
    if (areAllNecessaryImagesFilledOut) {
      if (this.photosModel.postInstallHeatingImages.length > 0) {
        if (this.photosModel?.postInstallHeatingImages[this.photosModel.postInstallHeatingImages.length - 1]?.image !== null) {
          this.photosModel.postInstallHeatingImages.push({image: null});
        }
      } else {
        this.photosModel.postInstallHeatingImages.push({image: null});
      }
    }
    this.shareService.photos = this.photosModel;
  }

  ngOnDestroy(): void {
    this.inputSubscriptions.forEach(subscription => subscription.unsubscribe());
    if (this.modelSub) {
      this.modelSub.unsubscribe();
    }
    if (this.photosSub) {
      this.photosSub.unsubscribe();
    }
  }
}
